<script setup lang="ts">
import { useI18n } from '@/composable/useI18n'
import { useRateStore } from '@/store/rate'
import { computed } from 'vue'

const { t } = useI18n()

const rateStore = useRateStore()

const rateData = computed(() => rateStore.getRateData)
const fareData = computed(() => rateStore.getFareData)

const totalOffPeak = computed(() => {
  return rateData?.value?.energy_sensors_totalizers.reduce((acc, sensor) => {
    return acc + (sensor.totalizer.consumption.off_peak_consumption_kwh / fareData.value.off_peak_consumption?.amount) * 100
  }, 0)
})

const totalPeak = computed(() => {
  return rateData?.value?.energy_sensors_totalizers.reduce((acc, sensor) => {
    return acc + (sensor.totalizer.consumption.peak_consumption_kwh / fareData.value.peak_consumption?.amount) * 100
  }, 0)
})

const totalMeasuredOffPeak = computed(() => {
  return rateData?.value?.energy_sensors_totalizers.reduce((acc, sensor) => {
    return acc + (sensor.totalizer.demand.off_peak_demand_kw / fareData.value.measured_off_peak_demand?.amount) * 100
  }, 0)
})

const totalMeasuredPeak = computed(() => {
  return rateData?.value?.energy_sensors_totalizers.reduce((acc, sensor) => {
    return acc + (sensor.totalizer.demand.peak_demand_kw / fareData.value.measured_peak_demand?.amount) * 100
  }, 0)
})

const roundedTotalOffPeak = computed(() => Math.round((totalOffPeak.value || 0) * 10) / 10)
const roundedTotalPeak = computed(() => Math.round((totalPeak.value || 0) * 10) / 10)
const roundedTotalMeasuredOffPeak = computed(() => Math.round((totalMeasuredOffPeak.value || 0) * 10) / 10)
const roundedTotalMeasuredPeak = computed(() => Math.round((totalMeasuredPeak.value || 0) * 10) / 10)
</script>

<template>
  <div class="flex flex-col gap-4 bg-white p-4 shadow-md">
    <h1 class="font-semibold text-black">{{ t('rate.group_table.title') }}</h1>
    <table class="w-full">
      <thead>
        <tr class="bg-slate-200">
          <th class="p-2 text-left"></th>
          <th class="p-2 text-left">{{ t('rate.group_table.off_peak') }}</th>
          <th class="p-2 text-left">{{ t('rate.group_table.peak') }}</th>
          <th class="p-2 text-left">{{ t('rate.group_table.off_peak_demand') }}</th>
          <th class="p-2 text-left">{{ t('rate.group_table.peak_demand') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(energySensor, index) in rateData?.energy_sensors_totalizers" :key="index">
          <td class="p-2 font-semibold text-black">{{ energySensor.sensor_name }}</td>
          <td class="p-2 font-medium text-black">
            {{ Math.round((energySensor.totalizer.consumption.off_peak_consumption_kwh / fareData.off_peak_consumption?.amount) * 100 * 10) / 10 }}%
          </td>
          <td class="p-2 font-medium text-black">
            {{ Math.round((energySensor.totalizer.consumption.peak_consumption_kwh / fareData.peak_consumption?.amount) * 100 * 10) / 10 }}%
          </td>
          <td class="p-2 font-medium text-black">
            {{ Math.round((energySensor.totalizer.demand.off_peak_demand_kw / fareData.measured_off_peak_demand?.amount) * 100 * 10) / 10 }}%
          </td>
          <td class="p-2 font-medium text-black">
            {{ Math.round((energySensor.totalizer.demand.peak_demand_kw / fareData.measured_peak_demand?.amount) * 100 * 10) / 10 }}%
          </td>
        </tr>
        <tr>
          <td class="p-2 font-semibold text-black">Total</td>
          <td class="p-2 font-medium text-black">{{ roundedTotalOffPeak }}%</td>
          <td class="p-2 font-medium text-black">{{ roundedTotalPeak }}%</td>
          <td class="p-2">{{ roundedTotalMeasuredOffPeak }}%</td>
          <td class="p-2">{{ roundedTotalMeasuredPeak }}%</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
